import { useAuth } from "@clerk/clerk-react";
import {
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import pic from "assets/landing/repitching_tag.jpeg";
import { OrganizationUserContext } from "contexts/Organization";
import { SplashPage } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router";
import { AddOnItemName } from "schemas/dashboard";
import { routes } from "schemas/routes";

import AuthForm from "components/Auth";
import { PAGE_VISITED } from "constants/trackingProps";
import { AuthAction } from "features/Auth/schema";
import { getParams, getSource, trackEvent } from "utils/tracking";
import { useLogin } from "utils/useLogin";
import { useSubscription } from "utils/useSubscription";

import { getRedirectUrl } from "../helpers";
import IntakeForm from "./IntakeForm";
import ProgramInfo from "./ProgramInfo";

const Repitching = () => {
  const location = useLocation();
  const isSignedIn = getParams("signed_in") === "true";
  const plan = getParams("plan");
  const navigate = useNavigate();
  const source = getSource();

  const { isLoaded } = useAuth();
  const { fetchUserInfo, fetchUserLoading } = useLogin();

  const { currentOrg } = useContext(OrganizationUserContext);
  const { handleCheckout } = useSubscription();

  const [selectedPlan, setSelectedPlan] = useState<AddOnItemName | null>(
    AddOnItemName.repitching_tier_two,
  );

  const isMobileScreen = useMediaQuery("(max-width:1200px)");
  const successCheckout = getParams("checkout") === "success";
  const redirectUrl = getRedirectUrl(
    routes.repitching,
    location,
    `plan=${selectedPlan}`,
  );
  const checkoutAddOn = (plan: AddOnItemName) => {
    handleCheckout(
      plan,
      `/${routes.repitching}`,
      source || "",
      SplashPage.repitching,
    );
  };

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    if (!currentOrg?.id) {
      fetchUserInfo();
    } else if (isSignedIn && plan) {
      checkoutAddOn(plan as AddOnItemName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, currentOrg?.id, plan]);

  useEffect(() => {
    if (successCheckout) {
      navigate(`/${routes.repitchingIntakeForm}${location.search}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successCheckout]);

  return fetchUserLoading || !isLoaded ? (
    <Backdrop
      sx={{
        backgroundColor: "white",
        zIndex: 9999,
        position: "absolute",
      }}
      open={fetchUserLoading}
      appear={false}
    >
      <CircularProgress />
    </Backdrop>
  ) : (
    <Grid
      container
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: "auto",
        justifyContent: "center",
        backgroundColor: "yellow.light",
      }}
    >
      {!isMobileScreen && (
        <Grid
          container
          item
          xs={5}
          sx={{
            backgroundImage: `url(${pic})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h2"
              sx={{ width: 100, color: "white", fontSize: 55, fontWeight: 700 }}
            >
              Your Personal Account Manager.
            </Typography>
          </Box>
        </Grid>
      )}

      <Grid
        item
        xs
        md
        lg
        container
        justifyContent={"center"}
        alignItems={"center"}
      >
        <Routes>
          <Route
            path="/"
            element={
              <ProgramInfo
                checkoutAddOn={checkoutAddOn}
                selectedPlan={selectedPlan}
                setSelectedPlan={setSelectedPlan}
              />
            }
          />
          <Route
            path="/sign-in/*"
            element={
              <AuthForm
                redirectUrl={redirectUrl}
                action={AuthAction.SIGN_IN}
                signInUrl={`/${routes.repitching}/sign-in${location.search}`}
                signUpUrl={`/${routes.repitching}/sign-up${location.search}`}
              />
            }
          />
          <Route
            path="/sign-up/*"
            element={
              <AuthForm
                redirectUrl={redirectUrl}
                action={AuthAction.SIGN_UP}
                signInUrl={`/${routes.repitching}/sign-in${location.search}`}
                signUpUrl={`/${routes.repitching}/sign-up${location.search}`}
              />
            }
          />
          <Route path="/intake-form/*" element={<IntakeForm />} />
        </Routes>
      </Grid>
    </Grid>
  );
};

export default Repitching;
